import { useCallback, useEffect, useState } from "react";
import { listReceipts, verifyReceipt } from "../lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, RefreshCcw } from "lucide-react";
import { ActiveSectorPill } from "../components/SectorPicker";

function short(h) {
    if (!h) return "—";
    if (h.length <= 18) return h;
    return `${h.slice(0, 14)}…${h.slice(-6)}`;
}

function modeCell(row) {
    if (row.live)
        return { label: "live", className: "text-emerald-700" };
    if (row.mode === "simulation")
        return { label: "synthetic", className: "text-amber-700" };
    return { label: "pending key", className: "text-slate-600" };
}

export default function Receipts() {
    const [rows, setRows] = useState(null);
    const [verifying, setVerifying] = useState(null);

    const reload = useCallback(
        () => listReceipts().then((d) => setRows(d.receipts)),
        [],
    );

    useEffect(() => {
        reload();
    }, [reload]);

    const onVerify = async (run_id) => {
        setVerifying(run_id);
        try {
            await verifyReceipt(run_id);
            await reload();
        } finally {
            setVerifying(null);
        }
    };

    return (
        <div data-testid="receipts-page">
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-10">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <div className="overline">Receipt history</div>
                        <span className="w-8 h-px bg-slate-300 hidden md:inline" />
                        <ActiveSectorPill />
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-3xl">
                        Every governed action,{" "}
                        <span className="text-slate-500">cryptographically receipted.</span>
                    </h1>
                    <p className="mt-4 text-base text-slate-700 max-w-3xl leading-relaxed">
                        KATLAS receives only governance metadata and evidence references.
                        Raw private data and withheld evidence never leave the originating
                        custody. Use the verify action to re-check a receipt against the
                        node at any time.
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                {rows === null ? (
                    <div className="text-slate-500">Loading…</div>
                ) : rows.length === 0 ? (
                    <div
                        className="tile rounded-sm p-10 text-center"
                        data-testid="receipts-empty"
                    >
                        <ShieldCheck
                            size={28}
                            strokeWidth={1.5}
                            className="mx-auto text-slate-400 mb-3"
                        />
                        <h3 className="text-xl font-bold tracking-tight">No receipts yet</h3>
                        <p className="text-sm text-slate-600 mt-2">
                            Run a scenario to produce the first KATLAS-signed receipt.
                        </p>
                        <Link
                            to="/"
                            className="btn-primary inline-block mt-5 text-sm px-5 py-2.5 rounded-sm"
                            data-testid="empty-start-scenario"
                        >
                            Start a scenario
                        </Link>
                    </div>
                ) : (
                    <div
                        className="tile rounded-sm overflow-hidden"
                        data-testid="receipts-table"
                    >
                        <table className="w-full text-sm">
                            <thead className="surface-subtle border-b border-slate-200">
                                <tr className="text-left">
                                    <Th>Decided</Th>
                                    <Th>Scenario</Th>
                                    <Th>Sector</Th>
                                    <Th>Decision</Th>
                                    <Th>Mode</Th>
                                    <Th>Hash</Th>
                                    <Th>Verification</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.run_id}
                                        className="border-t border-slate-100"
                                        data-testid={`receipt-row-${r.run_id}`}
                                    >
                                        <Td className="font-mono text-[12px] text-slate-600">
                                            {r.decided_at?.slice(0, 19).replace("T", " ")}
                                        </Td>
                                        <Td>{r.scenario_title}</Td>
                                        <Td className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">
                                            {r.sector?.short_label || r.sector?.label || "—"}
                                        </Td>
                                        <Td>{r.decision?.decision_label}</Td>
                                        <Td>
                                            <span
                                                className={`font-mono text-[11px] uppercase tracking-[0.18em] ${modeCell(r).className}`}
                                            >
                                                {modeCell(r).label}
                                            </span>
                                        </Td>
                                        <Td className="font-mono text-[12px]">{short(r.receipt_hash)}</Td>
                                        <Td className="font-mono text-[12px]">
                                            {r.verification_status}
                                        </Td>
                                        <Td>
                                            <button
                                                onClick={() => onVerify(r.run_id)}
                                                disabled={verifying === r.run_id}
                                                className="btn-ghost text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5"
                                                data-testid={`verify-${r.run_id}`}
                                            >
                                                <RefreshCcw
                                                    size={12}
                                                    strokeWidth={1.5}
                                                    className={
                                                        verifying === r.run_id ? "animate-spin" : ""
                                                    }
                                                />
                                                Re-verify
                                            </button>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const Th = ({ children }) => (
    <th className="px-4 py-3 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-600">
        {children}
    </th>
);
const Td = ({ children, className = "" }) => (
    <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);
